import { observable, computed } from 'mobx'
import _ from 'underscore'
import { t } from 'i18next'

import { newerThanCutoffDate, IDateFormatter } from './DateUtilities'
import { DBObject } from './DBObject'
import { FfmpegParameters } from './FfmpegParameters'
import { IDB } from './IDB'
import { MediaSlice } from './MediaSlice'
import { Passage } from './Passage'
import { PassageGloss } from './PassageGloss'
import { PassageHighlight } from './PassageHighlight'
import { PassageNote } from './PassageNote'
import { PassageSegment } from './PassageSegment'
import { PassageSegmentLabel } from './PassageSegmentLabel'
import { ReferenceMarker } from './ReferenceMarker'
import { PassageVideoMarker, PassageVideoMarkerType } from './PassageVideoMarker'
import { Project } from './Project'
import { Timeline } from './RootBase'
import { limit, normalizeUsername, remove, smallNoteMarker, largeNoteMarker } from './Utils'

import { fmt } from '../components/utils/Fmt'
import { ViewableVideoCollection } from '../components/video/ViewableVideoCollection'

import { RefRange } from '../scrRefs/RefRange'
import { refRangesToDisplay } from '../scrRefs/Utils'

import { Portion } from './Portion'
import { BiblicalTermMarker } from './BiblicalTermMarker'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const log = require('debug')('sltt:Models')

const intest = localStorage.getItem('intest') === 'true'

export interface IDrawableSegment {
    segment: PassageSegment
    time: number
}

export class VideoSlice {
    constructor(public video: PassageVideo, public position: number, public endPosition: number, public src: string) {}
}

type Marker = ReferenceMarker | BiblicalTermMarker
export class PassageVideo extends DBObject {
    url = ''

    duration = 0 // Duration as filmed.

    @observable computedDuration = 0 // Duration taking into account adjustments to segment length.

    isPatch = false

    @observable ffmpegParametersUsed?: FfmpegParameters

    @observable version = 0

    // This field is taken from the 'id' (NOT! _id) field of ProjectTask.
    @observable status = ''

    @observable _rev = 0

    @observable notes: PassageNote[] = []

    @observable segments: PassageSegment[] = []

    @observable glosses: PassageGloss[] = []

    @observable highlights: PassageHighlight[] = []

    @observable references: ReferenceMarker[] = []

    @observable biblicalTermMarkers: BiblicalTermMarker[] = []

    // email address of people who have viewed this
    @observable viewedBy: string[] = []

    @observable uploaded = false

    @observable mimeType = ''

    // These items are not persisted in online store
    @computed get isCompressed() {
        return !!this.ffmpegParametersUsed
    }

    @computed get resolution() {
        const resElement = this.ffmpegParametersUsed?.videoFilters.find((el) => el.startsWith('scale'))
        if (resElement === undefined) {
            return -1
        }

        const num = parseInt(resElement.slice(resElement.search(':') + 1))
        if (isNaN(num) || !isFinite(num)) {
            return -1
        }

        return num
    }

    @computed get crf() {
        if (!this.ffmpegParametersUsed) {
            return -1
        }
        const searchString = '-crf '
        const crfElement = this.ffmpegParametersUsed.outputOptions.find((el) => el.startsWith(searchString))
        if (crfElement === undefined) {
            return -1
        }

        const num = parseInt(crfElement.slice(searchString.length))
        if (isNaN(num) || !isFinite(num)) {
            return -1
        }

        return num
    }

    segmentTimesSet = false

    constructor(_id: string, db: IDB, creationDate?: Date) {
        super(_id, db)
        let creationDateString = this.creationDate
        if (creationDate) {
            creationDateString = db.getDate(creationDate)
        }
        this.creationDate = creationDateString
        this.setUMTRank()

        this.addSegment = this.addSegment.bind(this)
        this.timeToPosition = this.timeToPosition.bind(this)
    }

    toSnapshot() {
        const snapshot = this.toDocument()
        snapshot.segments = this.segments.map((segment) => segment.toSnapshot())
        snapshot.notes = this.notes.map((note) => note.toSnapshot())
        snapshot.glosses = this.glosses.map((gloss) => gloss.toSnapshot())

        return snapshot
    }

    resetSegmentTimes() {
        this.segmentTimesSet = false
    }

    toDocument() {
        const { url, duration, status, isPatch, version, viewedBy, uploaded, rank, ffmpegParametersUsed, mimeType } =
            this
        return this._toDocument({
            url,
            duration,
            status,
            isPatch,
            version,
            viewedBy,
            uploaded,
            rank,
            ffmpegParametersUsed: JSON.stringify(ffmpegParametersUsed),
            mimeType
        })
    }

    dbg(passage: Passage | null, details?: string) {
        const doc = this.toDocument()

        if (details?.includes('n')) {
            doc.notes = this.notes.map((note) => note.dbg())
        }

        if (details?.includes('g')) {
            doc.glosses = this.glosses.map((gloss) => gloss.dbg())
        }

        if (details?.includes('s')) {
            doc.segments = this.segments.map((segment) => segment.dbg(passage, details))
        } else {
            doc.segments = this.dbgSegments()
        }

        return doc
    }

    dbgSegments() {
        return this.segments.map((s, i) => ({
            segment: `${i + 1} time=${s.time?.toFixed(2)} pos=[${s.position?.toFixed(2)}..${s.endPosition?.toFixed(
                2
            )}]`,
            videoPatchHistory: s.videoPatchHistory
        }))
    }

    copy() {
        let copy = new PassageVideo(this._id, this.db, new Date(this.creationDate))
        copy = Object.assign(copy, this)
        if (this.ffmpegParametersUsed) {
            copy.ffmpegParametersUsed = this.ffmpegParametersUsed.copy()
        }
        copy.viewedBy = Array.from(this.viewedBy)
        copy.segments = this.segments.map((segment) => segment.copy())
        copy.references = this.references.map((ref) => ref.copy())
        copy.notes = this.notes.map((note) => note.copy())
        copy.glosses = this.glosses.map((gloss) => gloss.copy())
        copy.highlights = this.highlights.map((hgh) => hgh.copy())
        return copy
    }

    async setIsPatch() {
        if (this.isPatch) {
            return
        }
        const doc = this._toDocument({})
        doc.isPatch = true
        await this.db.put(doc)
    }

    async updateVersion() {
        const doc = this._toDocument({})
        doc.version = this.version + 1
        await this.db.put(doc)
    }

    async addViewedBy(email: string) {
        const { viewedBy } = this
        email = normalizeUsername(email)

        if (email === '' || this.creator === email || viewedBy.includes(email)) return

        const doc = this._toDocument({})
        // Force the _id for a task change to be different than the _id for the base passage
        doc._id += '@viewedby'

        doc.viewedByUser = email
        await this.db.put(doc)
    }

    // Only used when fixing incorrect duration
    async setDuration(duration: number) {
        if (duration === this.duration) {
            return
        }
        log('setDuration', fmt({ duration }))

        const doc = this._toDocument({})
        doc.duration = duration
        await this.db.put(doc)

        const segment = this.segments[0]
        await segment?.setPositions(0, duration, this)
    }

    createNote(position: number) {
        const creationDate = this.db.getNewId(this.notes, new Date(Date.now()))
        const _id = `${this._id}/${creationDate}`
        const note = new PassageNote(_id, this.db)
        if (intest) position = Math.round(position * 1000) / 1000 // stop jitter from failing test

        note.position = position
        note.setDefaultStartPosition()
        note.setDefaultEndPosition(this.computedDuration)

        return note
    }

    createNoteFromExisting(note: PassageNote) {
        if (note.removed) {
            return
        }

        const newNote = this.createNote(note.position)
        const copy = note.copy()
        copy._id = newNote._id
        copy.items = []
        return copy
    }

    get resolvedNotes() {
        return this.notes.filter((note) => note.resolved).sort((a, b) => a.position - b.position)
    }

    get unresolvedNotes() {
        return this.notes.filter((note) => !note.resolved).sort((a, b) => a.position - b.position)
    }

    async setStatus(status: string) {
        log('setStatus', status)
        if (status === this.status) {
            return
        }

        const doc = this._toDocument({})
        doc.status = status
        await this.db.put(doc)
    }

    async addNote(note: PassageNote): Promise<PassageVideo> {
        const noteExists = this.notes.find((n) => note._id === n._id)
        if (noteExists) {
            return this
        }

        note.rank = DBObject.numberToRank(note.position)
        await this.db.put(note.toDocument())

        const _note = this.notes.find((n) => note._id === n._id)
        if (!_note) {
            throw Error('Could not find note we just created')
        }
        return this
    }

    async removeNote(_id: string) {
        await remove(this.notes, _id)
    }

    createSegment(position: number) {
        const newId = this.db.getNewId(this.segments, new Date(Date.now()), 'seg_')
        const pvsg = new PassageSegment(`${this._id}/${newId}`, this.db)
        pvsg.position = position
        pvsg.rank = DBObject.numberToRank(position)
        return pvsg
    }

    createSegmentFromExisting(segment: PassageSegment) {
        if (segment.removed) {
            return
        }

        const segmentCopy = this.createSegment(segment.position)
        const copy = segment.copy()
        copy._id = segmentCopy._id
        copy.labels = []
        copy.glosses = []
        copy.documents = []
        copy.audioClips = []
        copy.resources = []

        return copy
    }

    async addSegmentFromExisting(segment: PassageSegment) {
        segment.rank = DBObject.numberToRank(segment.position)
        await this.db.put(segment.toDocument())
        const _segment = this.segments.find((s) => s._id === segment._id)
        if (!_segment) {
            throw new Error('Could not find segment we just inserted')
        }
        return _segment
    }

    async addSegment(position: number, labels: PassageSegmentLabel[] = [], references: RefRange[] = [], cc = '') {
        // If segment to be added is within .1 second of an existing segment, ignore add
        const closeToStart = (ps: PassageSegment) => ps.position + 0.1 >= position && ps.position - 0.1 <= position
        const closeToEnd = (ps: PassageSegment) => ps.endPosition + 0.1 >= position && ps.endPosition - 0.1 <= position
        const closeTo = (ps: PassageSegment) => closeToStart(ps) || closeToEnd(ps)

        // If the new segment is very close to an existing segment, just use existing segment
        let segmentIndex = this.segments.findIndex(closeTo)
        if (segmentIndex >= 0) {
            return { segmentIndex, segment: this.segments[segmentIndex] }
        }

        const pvsg = this.createSegment(position)
        pvsg.labels = labels
        pvsg.references = references
        pvsg.cc = cc

        // If there are no segments yet, the new segment always goes to end of video
        if (this.segments.length === 0) {
            pvsg.endPosition = this.duration
            await this.db.put(pvsg.toDocument())
            segmentIndex = this.segments.findIndex((s) => s._id === pvsg._id)
            if (segmentIndex === -1) {
                throw Error('Could not find added segment')
            }
            return { segmentIndex, segment: this.segments[segmentIndex] }
        }

        const currentSegment = this.segments
            .slice()
            .reverse()
            .find((s) => s.position < position)
        if (!currentSegment) {
            throw Error(`could not find segment to insert into, position=${position}`)
        }

        pvsg.endPosition = currentSegment.endPosition
        await this.db.put(pvsg.toDocument())

        await currentSegment.setEndPosition(position, this)

        segmentIndex = this.segments.findIndex((s) => s._id === pvsg._id)
        if (segmentIndex === -1) throw Error('Could not find added segment')

        return { segmentIndex, segment: this.segments[segmentIndex] }
    }

    async removeSegment(_id: string) {
        const index = this.segments.findIndex((s) => s._id === _id)
        if (index < 0 || this.isPatch) return
        const segment = this.segments[index]
        const previousIndex = index - 1
        await remove(this.segments, _id)
        if (previousIndex >= 0) {
            await this.segments[previousIndex].setEndPosition(segment.endPosition, this)
        }
    }

    // Adjust the position of the current segment and the previous one when there
    // isn't a gap between them.
    async saveSurroundingSegmentPositions(passage: Passage, segmentTime: number, segment: PassageSegment) {
        const exists = passage.findSegment(segment._id) !== undefined
        if (!exists) {
            return
        }

        const baseVideo = this.baseVideo(passage) || this
        const newPosition = baseVideo.timeToPosition(passage, segmentTime)
        const index = baseVideo.segments.findIndex((s) => s._id === segment._id)
        const previousSegment = baseVideo.segments[index - 1]

        // Update segment position and endPosition in a way that will prevent the
        // updates from being rejected.
        if (newPosition > segment.position) {
            await segment.setPositions(newPosition, segment.endPosition, baseVideo)
            if (previousSegment) {
                await previousSegment.setPositions(previousSegment.position, newPosition, baseVideo)
            }
        } else {
            if (previousSegment) {
                await previousSegment.setPositions(previousSegment.position, newPosition, baseVideo)
            }
            await segment.setPositions(newPosition, segment.endPosition, baseVideo)
        }
    }

    // create the segments for this selection
    async createSelectionSegment(passage: Passage, timeline: Timeline) {
        const { selectionStartTime, selectionEndTime } = timeline.getSelectionTimes()
        const startPosition = this.timeToPosition(passage, selectionStartTime)
        const endPosition = this.timeToPosition(passage, selectionEndTime)
        if (startPosition === undefined || endPosition === undefined) {
            throw Error('*Could not determine selection position')
        }

        await this.addSegment(endPosition)
        const { segment, segmentIndex } = await this.addSegment(startPosition)
        return { segment, segmentIndex }
    }

    // Return true if a patch could be created from startTime to endTime on passageVideo.
    patchable(timeline: Timeline, _displayError?: (message: string) => void) {
        const { selectionStartTime, selectionEndTime } = timeline.getSelectionTimes()
        const startIndex = this.timeToSegmentIndex(selectionStartTime)
        const endIndex = this.timeToSegmentIndex(selectionEndTime)

        if (startIndex === -1 || endIndex === -1) {
            if (_displayError) _displayError(t('Something went wrong - could not find selection.'))
            return false
        }

        if (startIndex !== endIndex) {
            if (_displayError) _displayError(t('Selection must not include more than one segment.'))
            return false
        }

        if (this.segments[startIndex].isPatched) {
            if (_displayError)
                _displayError(t('Cannot patch a selection in a patched segment. Delete the existing patches first.'))
            return false
        }

        return true
    }

    /* Common Marker code */
    async addMarkerFromExisting(marker: Marker, useExistingModDate?: boolean) {
        if (marker instanceof ReferenceMarker) {
            marker.rank = DBObject.numberToRank(marker.position)
            return this.db.put(marker.toDocument(useExistingModDate))
        }

        if (marker instanceof BiblicalTermMarker) {
            return this.db.put(marker.toDocument(useExistingModDate))
        }

        throw Error('Could not addMarkerFromExisting', marker)
    }

    createMarkerFromExisting(marker: Marker) {
        if (marker.removed) {
            return
        }

        const newMarker =
            marker instanceof ReferenceMarker
                ? this.createReference(marker.position)
                : this.createBiblicalTermMarker(new Date(marker.creationDate))

        const copy = marker.copy()
        copy._id = newMarker._id
        return copy
    }

    async copyMarkers(oldSegment: PassageSegment, markers: Marker[]) {
        if (!this.segments.length) {
            return Promise.resolve([])
        }

        const copiedMarkers: Marker[] = []
        for (const marker of markers) {
            const copy = this.createMarkerFromExisting(marker)
            if (copy) {
                copy.creationDate = marker.creationDate
                copy.modDate = marker.modDate
                copy.position = marker.time - oldSegment.time // adjust copied marker positions so they are offset from the old segment
                copiedMarkers.push(copy)
            }
        }

        const updatedMarkers = PassageVideoMarker.adjustMarkerPositions(copiedMarkers, this.duration) as Marker[]
        return Promise.all(updatedMarkers.map((marker) => this.addMarkerFromExisting(marker, true)))
    }

    /* Gloss code. Deprecate? */
    createGloss() {
        const newId = this.db.getNewId(this.glosses, new Date(Date.now()), 'gls_')
        return new PassageGloss(`${this._id}/${newId}`, this.db)
    }

    createGlossFromExisting(gloss: PassageGloss) {
        if (gloss.removed) {
            return
        }

        const pg = this.createGloss()
        const copy = gloss.copy()
        copy._id = pg._id
        return copy
    }

    async addGlossFromExisting(gloss: PassageGloss) {
        gloss.rank = DBObject.numberToRank(gloss.position)
        await this.db.put(gloss.toDocument())
        if (!this.glosses.find((g) => g._id === gloss._id)) {
            log('new gloss not found', gloss._id)
        }
    }

    async addGloss(position: number, text: string) {
        // If segment to be added is within .1 second of an existing segment, ignore add
        const gap = (pg: PassageGloss) => Math.abs(pg.position - position) < 0.15
        if (this.glosses.find(gap)) {
            log(`addGloss reject gap=${gap}`)
            return
        }

        const pg = this.createGloss()
        pg.position = position
        pg.rank = DBObject.numberToRank(position)
        pg.text = text

        log('addGloss', pg)
        await this.db.put(pg.toDocument())
    }

    // Used from command line to copy glosses for testing
    async copyGlosses(glosses: PassageGloss[]) {
        for (const gloss of glosses) {
            const { position, text } = gloss
            if (position <= this.duration) {
                await this.addGloss(position, text)
            }
        }
    }

    async removeGloss(_id: string) {
        await remove(this.glosses, _id)
    }

    /* Biblical Term code */
    createBiblicalTermMarker(creationDate?: Date) {
        const date = creationDate ?? new Date(Date.now())
        const newId = this.db.getNewId(this.biblicalTermMarkers, date, 'btm_')
        return new BiblicalTermMarker(`${this._id}/${newId}`, this.db, date)
    }

    async addBiblicalTermMarker(targetGlossId: string, position: number) {
        const tolerance = 0.2
        const gap = (ktm: BiblicalTermMarker) => Math.abs(ktm.position - position) < tolerance
        if (this.biblicalTermMarkers.find(gap)) {
            log(`addBiblicalTermMarker reject gap=${tolerance}`)
            return undefined
        }

        const marker = this.createBiblicalTermMarker()
        marker.targetGlossId = targetGlossId
        marker.position = position

        const doc = marker.toDocument()
        this.db.submitChange(doc)

        return this.biblicalTermMarkers.find((ktm) => ktm._id === marker._id)
    }

    async removeBiblicalTermMarker(_id: string) {
        const idx = _.findIndex(this.biblicalTermMarkers, { _id })
        if (idx < 0) return

        const item = this.biblicalTermMarkers[idx]

        const doc = item._toDocument({})
        doc.removed = true
        this.db.submitChange(doc)
    }

    /* Verse Reference Marker code */
    createReference(position: number) {
        const newId = this.db.getNewId(this.references, new Date(Date.now()), 'ref_')
        const ref = new ReferenceMarker(`${this._id}/${newId}`, this.db)
        ref.position = position
        ref.rank = DBObject.numberToRank(position)
        return ref
    }

    async addNextReferenceInSequence(portion: Portion, passage: Passage, currentTime: number, versification: string) {
        const video = this.timeToVideo(passage, currentTime)
        const position = video.timeToPosition(passage, currentTime)
        const baseVideo = this.baseVideo(passage) || this
        const refRanges = baseVideo.getRefRanges(passage, currentTime)
        const defaultVerse = '001001001'
        let references = [new RefRange(defaultVerse, defaultVerse)]
        if (refRanges.length) {
            const previousReference = refRanges[refRanges.length - 1]
            const nextVerse = RefRange.nextVerse(previousReference.endRef, versification)
            references = [new RefRange(nextVerse, nextVerse)]
        } else if (passage.references.length) {
            references = passage.references
        } else if (portion.references.length) {
            references = portion.references
        }
        const reference = await video.addReference(references, position)
        return reference
    }

    async addReference(reference: RefRange[], position: number) {
        const gap = (ref: ReferenceMarker) => Math.abs(ref.position - position) < ReferenceMarker.minDistance
        if (this.references.find(gap)) {
            log(`addReference reject gap=${ReferenceMarker.minDistance}`)
            return undefined
        }

        const ref = this.createReference(position)
        ref.references = reference

        log('addReference', JSON.stringify(reference))
        const doc = ref.toDocument()
        this.db.submitChange(doc)

        // Make sure we have latest "reference" to ref
        const _ref = this.references.find((r) => r._id === ref._id)
        if (_ref) {
            _ref.newlyCreated = true
        }

        return _ref
    }

    async saveMarkerPosition(passage: Passage, time: number, marker: ReferenceMarker | BiblicalTermMarker) {
        return marker instanceof ReferenceMarker
            ? this.saveReferencePosition(passage, time, marker)
            : this.saveBiblicalTermMarkerPosition(passage, time, marker)
    }

    // Set the position of a verse reference, handling the case where the reference
    // is moved to another video within the same base video.
    async saveReferencePosition(passage: Passage, time: number, reference: ReferenceMarker) {
        const exists = passage.videos.some((vid) => vid.references.find((ref) => ref._id === reference._id))
        const oldVideo = passage.findVideo(reference._id)
        if (!exists || !oldVideo) {
            return
        }

        const baseVideo = this.baseVideo(passage) || this
        const video = baseVideo.timeToVideo(passage, time)
        const position = video.timeToPosition(passage, time)

        let _reference: ReferenceMarker | undefined
        if (video._id !== oldVideo._id) {
            await oldVideo.removeReference(reference._id)
            _reference = await video.addReference(reference.references, position)
        } else {
            await reference.setPosition(position)
            _reference = video.references.find((ref) => ref._id === reference._id)
        }

        return _reference
    }

    async saveBiblicalTermMarkerPosition(passage: Passage, time: number, marker: BiblicalTermMarker) {
        const exists = passage.videos.some((vid) => vid.biblicalTermMarkers.find((ktm) => ktm._id === marker._id))
        const oldVideo = passage.findVideo(marker._id)
        if (!exists || !oldVideo) {
            return
        }

        const baseVideo = this.baseVideo(passage) || this
        const video = baseVideo.timeToVideo(passage, time)
        const position = video.timeToPosition(passage, time)

        let _marker: BiblicalTermMarker | undefined
        if (video._id !== oldVideo._id) {
            await oldVideo.removeBiblicalTermMarker(marker._id)
            _marker = await video.addBiblicalTermMarker(marker.targetGlossId, position)
        } else {
            await marker.setPosition(position)
            _marker = video.biblicalTermMarkers.find((ktm) => ktm._id === marker._id)
        }

        return _marker
    }

    async removeReference(_id: string) {
        const idx = _.findIndex(this.references, { _id })
        if (idx < 0) return

        const item = this.references[idx]

        const doc = item._toDocument({})
        doc.removed = true
        this.db.submitChange(doc)
    }

    createHighlight() {
        const newId = this.db.getNewId(this.glosses, new Date(Date.now()), 'hgh_')
        const ph = new PassageHighlight(`${this._id}/${newId}`, this.db)
        return ph
    }

    createHighlightFromExisting(highlight: PassageHighlight) {
        if (highlight.removed) {
            return
        }

        const newHighlight = this.createHighlight()
        const copy = highlight.copy()
        copy._id = newHighlight._id
        return copy
    }

    async addHighlightFromExisting(highlight: PassageHighlight) {
        await this.db.put(highlight.toDocument())
        if (!this.highlights.find((h) => h._id === highlight._id)) {
            log('new highlight not found', highlight._id)
        }
    }

    async addHighlight(color: number, firstId: string, lastId: string, resourceName: string) {
        // If the user selected the words by dragging right to left first and last will
        // be reversed, switch them
        if (firstId > lastId) {
            const temp = lastId
            lastId = firstId
            firstId = temp
        }

        if (color === 0) {
            // if this is a clear color request, seach throug all existing non clear color
            // highlights for this resourceName. If you don't find one that overlaps with
            // this range is nothing to clear, just return
            // Two ranges overlap if end1 >= start2 && end2 >= start1
            if (
                !this.highlights.find(
                    (h) => h.resourceName === resourceName && h.color && lastId >= h.firstId && h.lastId >= firstId
                )
            ) {
                log('addHighligh no-action-necessary')
                return
            }
        }

        const ph = this.createHighlight()
        ph.color = color
        ph.firstId = firstId
        ph.lastId = lastId
        ph.resourceName = resourceName

        await this.db.put(ph.toDocument())
    }

    // Convert specified time to a position offset in the segment containing that time
    timeToPosition(passage: Passage, time: number) {
        let segment: PassageSegment

        if (this.isPatch) {
            // Patch videos only have one segment
            segment = this.segments[0]
        } else {
            // Otherwise search for the segment by time
            segment = this.timeToSegment(passage, time)
            segment = segment.actualSegment(passage)
        }

        log('!!!', time, segment.time, segment.position, segment.endPosition)

        return limit(segment.position + time - segment.time, segment.position, segment.endPosition)
    }

    // Return videoPassage that plays at this time.
    // Either a manin video or a patch video (if the segment has been patched)
    timeToVideo(passage: Passage, time: number) {
        const segment = this.timeToSegment(passage, time)
        return segment.patchVideo(passage) || this
    }

    visibleSegments(passage: Passage) {
        return this.segments.map((segment) => segment.actualSegment(passage))
    }

    timeToSegment(passage: Passage, time: number) {
        const si = this.timeToSegmentIndex(time)
        // log(`timeToSegment[${this._id}] si=${si}`)
        return this.segments[si]
    }

    timeToSegmentIndex(time: number) {
        if (this.segments.length === 0) {
            throw Error('no segments present')
        }

        let index = this.segments.findIndex((s) => s.time > time)
        if (index === -1) {
            // If there is not segment with a position greater than this position
            // default to last segment
            index = this.segments.length - 1
        } else if (index > 0) {
            // If not the fist segment, use the segment before this
            index -= 1
        }
        return index
    }

    // returns { segment, segmentIndex }
    positionToSegment(position: number) {
        if (this.segments.length === 0) {
            throw Error('no segments present')
        }

        let segmentIndex = this.segments.findIndex((s) => s.position > position)
        if (segmentIndex === -1) {
            // If there is not segment with a position greater than this position
            // default to last segment
            segmentIndex = this.segments.length - 1
        } else if (segmentIndex > 0) {
            // If not the fist segment, use the segment before this
            segmentIndex -= 1
        }

        const segment = this.segments[segmentIndex]

        return { segment, segmentIndex }
    }

    // ??? this is using time for the base segment, not actual segment, is that OK???
    positionToTime(position: number) {
        const { segment } = this.positionToSegment(position)
        return segment.positionToTime(position)
    }

    // For a patch video find the video it is a patch of
    baseVideo(passage: Passage, dontLogError?: boolean) {
        if (!this.isPatch) return null

        for (const video of passage.videos) {
            if (video.segments.some((segment) => segment.videoPatchHistory.includes(this._id))) {
                return video
            }
        }

        if (this.removed) return null

        // Should we be throwing an exception here?
        // There is not much we can do about it.
        if (!dontLogError) {
            log(
                '### cannot find baseVideo for patch',
                fmt({
                    passage: passage.name,
                    id: `_${this._id}`,
                    creationDate: this.creationDate
                })
            )
        }

        return null
    }

    log(passage: Passage | null, label: string) {
        log(`[PassageVideo] ${label}`, JSON.stringify(this.dbg(passage, 's'), null, 4))
    }

    log2(tag: string, values: any) {
        const _id = this._id.split('/').slice(-1)[0]
        log(`PassageVideo[${_id}] ${tag}`, JSON.stringify(values, null, 4))
    }

    // Set the starting time in the main video timeline for each segment.
    setSegmentTimes(passage: Passage, forceReset?: boolean) {
        if (!forceReset && this.segmentTimesSet) return

        let time = 0

        if (this.isPatch) {
            // should not be calling this function for a patch segment since patch segment
            // times should be set by calling this function on the containing video
            console.error('setSegmentTimes called on patch segment!!!')
            return
        }

        this.segments.forEach((segment) => {
            let { duration } = segment
            segment.time = time

            // If there are patch video on this segment set all of them to start at the current
            // time. Save the duration of the last (onTop) segment as the duration of this
            // segment.
            for (const patchId of segment.videoPatchHistory) {
                const video = passage.findVideo(patchId)
                if (!video) {
                    log('setSegmentTimes no video!')
                    continue // should never happen
                }

                const firstSegment = video.segments[0]
                firstSegment.time = time
                duration = firstSegment.duration
            }

            time += duration
        })

        this.computedDuration = time // set duration based on total duration of segments
        // this.log2(`setSegmentTimes[${time.toFixed(2)}]`,
        //     this.segments.map(s => ({time: s.time.toFixed(2), duration: s.duration.toFixed(2)})))

        this.segmentTimesSet = true
    }

    // For all notes in this video (including notes on patches)
    // set the note time and segmentIndex.
    private setNoteTimes(passage: Passage) {
        for (const note of this.notes) {
            if (this.segments.length === 0) {
                // 2020-06-09 there is a bug that is causing video to have not segments
                // ignore these video for now
                log(`### video has no segments ${this._id}`)
                continue
            }

            const { segmentIndex, segment } = this.positionToSegment(note.position)

            note.segmentIndex = segmentIndex
            const time = segment.positionToTime(note.position)

            const actualSegment = segment.actualSegment(passage)
            const actualSegmentEndTime = actualSegment.positionToTime(actualSegment.endPosition)

            note.time = Math.min(time, actualSegmentEndTime)

            note.onTop = !segment.isPatched
            note.inIgnoredSegment = actualSegment.ignoreWhenPlayingVideo
        }

        for (const { patchVideo, onTop, segmentIndex, actualSegment } of this.patchVideos(passage)) {
            patchVideo.setupNotesForPatch(passage, actualSegment, segmentIndex, onTop)
        }
    }

    private setMarkerTimes(passage: Passage, markers: PassageVideoMarker[], markerType: PassageVideoMarkerType) {
        for (const marker of markers) {
            if (this.segments.length === 0) {
                log(`### video has no segments ${this._id}`)
                continue
            }

            const { segment, segmentIndex } = this.positionToSegment(marker.position)
            const time = segment.positionToTime(marker.position)

            const actualSegment = segment.actualSegment(passage)
            const actualSegmentEndTime = actualSegment.positionToTime(actualSegment.endPosition)

            marker.time = Math.min(time, actualSegmentEndTime)
            marker.segmentIndex = segmentIndex
        }

        for (const { patchVideo, actualSegment, segmentIndex } of this.patchVideos(passage)) {
            const patchVideoMarkers =
                markerType === 'reference' ? patchVideo.references : patchVideo.biblicalTermMarkers
            patchVideo.setupMarkersForPatch(actualSegment, segmentIndex, patchVideoMarkers)
        }
    }

    private setupMarkersForPatch(actualSegment: PassageSegment, segmentIndex: number, markers: PassageVideoMarker[]) {
        // Limit start and end times to match the top most (aka 'actual') patch

        const startTime = actualSegment.time
        const endTime = actualSegment.time + actualSegment.duration

        const segment = this.segments[0]

        markers.forEach((marker) => {
            const time = segment.positionToTime(marker.position)
            marker.time = limit(time, startTime, endTime)
            marker.segmentIndex = segmentIndex
        })
    }

    // Iterate over all videos which are patches to this base (i.e. !isPatch) video.
    // Ignores (with ###log message) any videos listed as patches in videoPatchHistory
    // but not actually found in passage
    *patchVideos(passage: Passage) {
        for (let segmentIndex = 0; segmentIndex < this.segments.length; ++segmentIndex) {
            const segment = this.segments[segmentIndex]
            const actualSegment = segment.actualSegment(passage)

            const { videoPatchHistory } = segment

            for (let i = 0; i < videoPatchHistory.length; ++i) {
                const videoId = videoPatchHistory[i]
                const patchVideo = passage.findVideo(videoId)
                if (!patchVideo) continue // ignore missing patch video

                const onTop = i === videoPatchHistory.length - 1

                yield { patchVideo, segmentIndex, actualSegment, onTop }
            }
        }
    }

    // Setup time and onTop attributes for all notes on this patch
    private setupNotesForPatch(passage: Passage, actualSegment: PassageSegment, segmentIndex: number, onTop: boolean) {
        // Limit start and end times to match the top most (aka 'actual') patch (since it

        const startTime = actualSegment.time
        const endTime = actualSegment.time + actualSegment.duration

        const segment = this.segments[0]

        this.notes.forEach((note) => {
            const time = segment.positionToTime(note.position)
            note.segmentIndex = segmentIndex
            note.time = limit(time, startTime, endTime)
            note.onTop = onTop
            note.inIgnoredSegment = actualSegment.ignoreWhenPlayingVideo
        })
    }

    // Find the most recent unviewed note. Unviewed notes are those created after
    // the specified cutoff date that are unviewed.
    firstUnviewedNoteAfterDate(passage: Passage, username: string, cutoff: Date, includeConsultantOnlyNotes: boolean) {
        const visibleNotes = this.getVisibleNotes(passage, includeConsultantOnlyNotes)
        let unviewedItems = visibleNotes.flatMap((note) =>
            note.unviewedItemsAfterDate(username, cutoff, includeConsultantOnlyNotes)
        )
        unviewedItems = _.sortBy(unviewedItems, 'creationDate')
        const newestUnviewedItem = unviewedItems[unviewedItems.length - 1]
        return newestUnviewedItem ? passage.findNote(newestUnviewedItem._id) || null : null
    }

    // Find the most recent unresolved note. Unresolved notes are those created after
    // the specified cutoff date that are unresolved.
    mostRecentUnresolvedNote(passage: Passage, cutoff: Date, includeConsultantOnlyNotes: boolean) {
        const visibleNotes = this.getVisibleNotes(passage, includeConsultantOnlyNotes)
        let unresolvedItems = visibleNotes
            .filter((note) => !note.resolved)
            .filter((note) => includeConsultantOnlyNotes || !note.consultantOnly)
            .flatMap((note) => note.items)
            .filter((item) => newerThanCutoffDate(item.creationDate, cutoff))
        unresolvedItems = _.sortBy(unresolvedItems, 'creationDate')
        const newestUnresolvedItem = unresolvedItems[unresolvedItems.length - 1]
        return newestUnresolvedItem ? passage.findNote(newestUnresolvedItem._id) || null : null
    }

    isUnviewedAfterDate(username: string, cutoff: Date) {
        const newerThanCutoff = newerThanCutoffDate(this.creationDate, cutoff)
        const isCreator = this.creator === username
        const hasViewed = this.viewedBy.includes(username)
        return newerThanCutoff && !isCreator && !hasViewed
    }

    // Return a a list of all the notes for this video, sorted by ascending time
    // ??? does this need to deal with resolved status?
    getVisibleNotes(passage: Passage, showConsultantOnlyNotes: boolean) {
        if (!this.verifyIsBaseVideo()) return []

        let notes = [...this.notes]
        for (const { patchVideo } of this.patchVideos(passage)) {
            notes = notes.concat(patchVideo.notes)
        }

        this.setSegmentTimes(passage) // must be called before setNoteTimes
        this.setNoteTimes(passage)

        notes = notes.filter((note) => showConsultantOnlyNotes || !note.consultantOnly)

        notes = _.sortBy(notes, 'time')
        // this.log('getVisibleNotes', {_: notes.map(note => note.time.toFixed(2))})

        return notes
    }

    verifyIsBaseVideo() {
        if (this.isPatch) {
            log(`### Is not base video ${this._id}`)
            return false
        }

        return true
    }

    // Return a a list of all the notes for this video with x/y/width values set
    // forceSmallMarkers is only used for unit testing
    getDrawableNotes(
        passage: Passage,
        noteBarWidth: number,
        showConsultantOnlyNotes: boolean,
        forceSmallMarkers?: boolean
    ) {
        const notes = this.getVisibleNotes(passage, showConsultantOnlyNotes)

        const useSmallMarkers = forceSmallMarkers || notes.length > 12

        const markerWidth = useSmallMarkers ? smallNoteMarker : largeNoteMarker
        notes.forEach((note) => (note.width = markerWidth))

        // Setup the x, y position for each note to make it not overlap other notes in the time line
        notes.forEach((note, index) => note.setupMarker(notes, index, this.computedDuration, noteBarWidth))

        this.log2('getDrawableNotes', {
            noteBarWidth,
            duration: this.computedDuration,
            _: notes.map((note) => note.x.toFixed(0))
        })

        return notes
    }

    getVisibleMarkers(passage: Passage, markerType: PassageVideoMarkerType) {
        if (!this.verifyIsBaseVideo()) return []

        let markers: PassageVideoMarker[] = []
        const passageVideoMarkers = markerType === 'reference' ? this.references : this.biblicalTermMarkers
        for (const marker of passageVideoMarkers) {
            const { segment } = this.positionToSegment(marker.position)
            if (!segment.isPatched) {
                markers.push(marker)
            }
        }

        for (const { patchVideo, onTop } of this.patchVideos(passage)) {
            if (!onTop) continue

            const patchVideoMarkers =
                markerType === 'reference' ? patchVideo.references : patchVideo.biblicalTermMarkers

            for (const marker of patchVideoMarkers) {
                markers.push(marker)
            }
        }

        this.setSegmentTimes(passage) // must be called before setReferenceTimes
        this.setMarkerTimes(passage, passageVideoMarkers, markerType)

        markers = _.sortBy(markers, 'time')
        return markers
    }

    getVisibleBiblicalTermMarkers(passage: Passage) {
        return this.getVisibleMarkers(passage, 'biblicalTerm') as BiblicalTermMarker[]
    }

    getVisibleReferenceMarkers(passage: Passage) {
        return this.getVisibleMarkers(passage, 'reference') as ReferenceMarker[]
    }

    // Return a a list of all the visible glosses for this base video, sorted by ascending time
    getVisibleGlosses(passage: Passage) {
        if (!this.verifyIsBaseVideo()) return []

        let glosses: PassageGloss[] = []
        this.setSegmentTimes(passage) // ensure segment times up to date

        for (const gloss of this.glosses) {
            const { segment, segmentIndex } = this.positionToSegment(gloss.position)
            // A gloss on the base video is visible if it starts in an unpatched segment
            if (!segment.isPatched) {
                gloss.segmentIndex = segmentIndex
                gloss.time = segment.positionToTime(gloss.position)
                glosses.push(gloss)
            }
        }

        // A gloss on a patch video is visible if the patch is visible
        for (const { patchVideo, onTop, segmentIndex } of this.patchVideos(passage)) {
            if (!onTop) continue // if patch is not visible, skip its glosses

            const segment = patchVideo.segments[0] // a patch has exactly one segment

            for (const gloss of patchVideo.glosses) {
                gloss.segmentIndex = segmentIndex
                gloss.time = segment.positionToTime(gloss.position)
                glosses.push(gloss)
            }
        }

        glosses = _.sortBy(glosses, 'time')

        return glosses
    }

    displayedCreationDate(dateFormatter: IDateFormatter) {
        const { creationDate, version } = this
        const date = new Date(creationDate)
        const base = dateFormatter.format(date)
        const ending = version > 0 ? `-${version}` : ''
        return `${base}${ending}`
    }

    findSegmentIndex(_id: string /* segment _id */, throwIfMissing?: boolean) {
        const segmentIndex = this.segments.findIndex((s) => s._id === _id)

        if (throwIfMissing && segmentIndex < 0) throw Error('Something went wrong. Could not find segment.')

        return segmentIndex
    }

    // Get the first non-empty reference that starts before the specified time
    getRefRanges(passage: Passage, currentTime: number) {
        const visibleReferences = this.getVisibleReferenceMarkers(passage)

        let references: RefRange[] = []
        for (const ref of visibleReferences) {
            if (ref.time > currentTime) {
                break
            }
            if (ref.references.length > 0) {
                references = ref.references
            }
        }

        return references
    }

    // comments
    // logging

    // create minimum number of slices of base video and latest patches that don't
    // have gaps between them
    createSlicesWithNoGaps(
        passage: Passage,
        selectionStartTime: number, // -1 if no selection in effect
        selectionEndTime: number // -1 if no selection in effect
    ) {
        const baseVideo = this.baseVideo(passage) || this
        const visibleSegments = baseVideo.segments.filter((seg) => !seg.actualSegment(passage).ignoreWhenPlayingVideo)
        const newSlices: VideoSlice[] = []

        for (const segment of visibleSegments) {
            const actualVideo = segment.actualVideo(passage) || baseVideo
            const actualSegment = segment.actualSegment(passage)
            let { position, endPosition } = actualSegment

            const { time } = actualSegment
            const endTime = actualSegment.time + actualSegment.endPosition - actualSegment.position
            log('createSlicesWithNoGaps', fmt({ actualVideo, position, endPosition, time, endTime }))

            // Skip zero length segments. This could have happened in older recordings.
            if (endPosition - position <= 0) {
                log('Found a zero length segment, so skipping...')
                continue
            }
            // If there is a selection ...
            // If this segment ends before the selection starts, skip the segment
            // If this segment starts before the selection starts, move the start position of
            // the segment forward.
            if (selectionStartTime > 0) {
                if (endTime <= selectionStartTime) continue
                if (time < selectionStartTime) {
                    position = position + selectionStartTime - time
                    log('trim position', fmt({ position }))
                }
            }

            // If there is a selection ...
            // If this segment starts after the selection ends, skip the segment
            // If this segment ends after the selection ends, move the end position of
            // the segment backward.
            if (selectionEndTime > 0) {
                if (time >= selectionEndTime) continue
                if (endTime > selectionEndTime) {
                    endPosition -= endTime - selectionEndTime
                    log('trim endPosition', fmt({ endPosition }))
                }
            }

            // If this segment is contiguous with the previous segment, adjust the end position of the
            // last slice
            const lastSlice = newSlices.slice(-1)[0] // undefined if no new slices created yet
            if (this.isContiguousVideo(lastSlice?.video._id, lastSlice?.endPosition, actualVideo._id, position)) {
                log(`adjust endPosition ${lastSlice.endPosition} = ${endPosition}`)
                lastSlice.endPosition = endPosition
            } else {
                const newSlice: VideoSlice = { video: actualVideo, position, endPosition, src: '' }
                log('newSlice', fmt(newSlice))
                newSlices.push(newSlice)
            }
        }

        return newSlices
    }

    createSlices(passage: Passage) {
        const baseVideo = this.baseVideo(passage) || this
        const visibleSegments = baseVideo.visibleSegments(passage)
        const slices = []
        for (const segment of visibleSegments) {
            const actualVideo = segment.actualVideo(passage)
            if (!actualVideo) {
                throw new Error('No video for this segment')
            }
            const actualSegment = segment.actualSegment(passage)
            const { position, endPosition } = actualSegment
            slices.push(new VideoSlice(actualVideo, position, endPosition, ''))
        }

        return slices
    }

    private isContiguousVideo(
        videoId1: string | undefined,
        endPosition: number | undefined,
        videoId2: string,
        position: number
    ) {
        if (videoId1 !== videoId2) return false

        const ALittleBit = 0.05
        const closeEnough = (a: number, b: number) => Math.abs(a - b) < ALittleBit

        return closeEnough(endPosition ?? -1, position)
    }

    async getPlayableSlices(passage: Passage, vvc: ViewableVideoCollection) {
        try {
            const baseVideo = this.baseVideo(passage) || this
            vvc.setup(passage, baseVideo)
            await vvc.download()
            await vvc.waitUntilDownloaded()
            const slices = baseVideo.createSlices(passage)
            const mediaSlices: MediaSlice[] = []
            for (const slice of slices) {
                const viewableVideo = vvc.viewableVideos.find((vv) => vv.video._id === slice.video._id)
                if (!viewableVideo) {
                    throw new Error('No viewable video for this slice')
                }
                mediaSlices.push(new MediaSlice(slice.position, slice.endPosition, viewableVideo.src))
            }

            return mediaSlices
        } catch (error) {
            return []
        }
    }

    getDrawableSegments(passage: Passage, timeline: Timeline) {
        let drawableSegments: IDrawableSegment[] = []
        for (let i = 0; i < this.segments.length; ++i) {
            const segment = this.segments[i]
            const actualSegment = segment.actualSegment(passage)
            const { time } = actualSegment

            if (time + actualSegment.duration < timeline.start) continue
            if (time > timeline.end) continue

            const nextEntry = { segment, time }
            drawableSegments.push(nextEntry)
        }

        drawableSegments = _.sortBy(drawableSegments, 'startX')
        return drawableSegments
    }

    getVisibleRefRanges(passage: Passage) {
        const references = this.getVisibleReferenceMarkers(passage)
        return references.flatMap((pvr) => pvr.references)
    }

    // Passage highlights are only stored on the base video, so there is no need to
    // look at any patch videos.
    getVisibleHighlights() {
        return this.highlights
    }

    isAudioOnly() {
        if (this.url.trim() === '') {
            return false
        }

        return this.mimeType.startsWith('audio')
    }

    getTranscription(passage: Passage, project: Project) {
        const visibleSegments = this.visibleSegments(passage)
        const visibleReferences = this.getVisibleReferenceMarkers(passage)
        let transcription = passage.references.length
            ? `|${refRangesToDisplay(passage.references, project)}|${visibleReferences.length ? '' : '\n'}`
            : ''
        visibleSegments.forEach((segment, segmentIndex) => {
            const referencesInSegment = visibleReferences.filter((ref) => ref.segmentIndex === segmentIndex)
            const segmentReference = referencesInSegment.length
                ? refRangesToDisplay(referencesInSegment.flatMap((ref) => ref.references, project))
                : ''
            const segmentTranscription = segment.transcriptions?.slice(-1)[0]?.text ?? ''
            transcription = segmentReference
                ? `${transcription}\n|${segmentReference}|\n${segmentTranscription} `
                : `${transcription}${segmentTranscription} `
        })
        return transcription.trim()
    }
}
